import DateFnsUtils from '@date-io/date-fns';
import { yupResolver } from '@hookform/resolvers';
import CardContents from '@ifca-root/react-component/src/components/CardList/CardContents';
import { AccountFooter } from '@ifca-root/react-component/src/components/Footer/AccountFooter';
import MainHeader from '@ifca-root/react-component/src/components/Header/MainHeader';
import { ContentWrapper } from '@ifca-root/react-component/src/components/Layout/ContentWrapper';
import Loading from '@ifca-root/react-component/src/components/Loading/Loading';
import {
  KeyboardDatePicker,
  MuiPickersUtilsProvider,
} from '@material-ui/pickers';
import { Checkbox, MenuItem, TextField } from '@material-ui/core';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import { Autocomplete } from '@material-ui/lab';
import { CommonYupValidation } from 'helpers/Form/YupValidation';
import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useHistory, useLocation, useParams } from 'react-router';
import * as yup from 'yup';
import {
  useGetAssignedCompanyQuery,
  useGetCompanyNameQuery,
  useGetFaCategoryDropdownListingQuery,
  useGetCostCentreCodeLazyQuery,
  useGetFaLocationLazyQuery,
  useGetAccountPeriodYearPeriodLazyQuery,
} from '@account-root/account-react/src/generated/graphql';
import NumberFormat from 'react-number-format'

interface FASummaryParamsProps {
    CompanyID: string;
    AssetType: string;
    Year: number;
    MonthFrom: number;
    MonthTo: number;
}

interface CompanyOption {
    Name: string;
    CompanyID: string;
}

export const FASummaryParamsForm = (props: any) => {
  let history = useHistory();
  const user = JSON.parse(localStorage.getItem('loggedInUser'));
  const accountID = user.accountID;
  const { CompanyID }: any = useParams();
  let location = useLocation();
  const editData = location?.state as any;
  const latestCompany = localStorage.getItem('latestCompany');
  const ParamsSchema = yup.object().shape({
    CompanyID: CommonYupValidation.requireField('Company is required'),
    Year: yup.string().required('Year is required'),
    MonthFrom: yup.string().required('Month From is required'),
    MonthTo: yup.string().required('Month To is required'),
  });
  const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
  const checkedIcon = <CheckBoxIcon fontSize="small" />;
  
  const {
    handleSubmit,
    register,
    errors,
    control,
    getValues,
    watch,
    setValue,
  } = useForm<FASummaryParamsProps>({
    defaultValues: {},
    mode: 'all',
    resolver: yupResolver(ParamsSchema),
  });

  const{
    loading: CompanyLoading,
    error: CompanyError,
    data: { getCompany } = { getCompany: [] },
  } = useGetCompanyNameQuery({
    variables: { CompanyID },
    fetchPolicy: 'network-only',
    onCompleted: () => {
      if (latestCompany) {
        loadCostCentre({
          variables: {
            CompanyID: latestCompany,
            orderByAsc: 'Code',
            IsLastNode: true,
          }
        })
        loadFALocation({
          variables: {
            CompanyID: latestCompany
          }
        })
      }
    },
  });

  const {
    loading: CompanyAssignedLoading,
    error: CompanyAssignedError,
    data: { getAssignedCompanyByUser } = { getAssignedCompanyByUser: [] },
  } = useGetAssignedCompanyQuery({
    fetchPolicy: 'network-only',
    onCompleted: () => {
      if (latestCompany) {
        loadCostCentre({
          variables: {
            CompanyID: latestCompany,
            orderByAsc: 'Code',
            IsLastNode: true,
          }
        })

        loadFALocation({
          variables: {
            CompanyID: latestCompany
          }
        })
      }
    },
  });

  const {
    loading: FACategoryDropdownListingLoading,
    error: FACategoryDropdownListingError,     
    data: { getFACategoryDropdownListing } = { getFACategoryDropdownListing: [] },
  } = useGetFaCategoryDropdownListingQuery({
    fetchPolicy: 'network-only',
  });

  const [
    loadCostCentre,
    {
      loading: CostCentreLoading,
      data: { getCostCentre } = {
        getCostCentre: []
      },
    },
  ] = useGetCostCentreCodeLazyQuery({
    fetchPolicy: 'network-only',
  })

  const [
    loadFALocation,
    {
      loading: FALocationLoading,
      data: { getFALocation } = {
        getFALocation: [],
      },
    },
  ] = useGetFaLocationLazyQuery({
    fetchPolicy: 'network-only',
  });

  const [selectedFACategory, setSelectedFACategory] = useState(new Set())

  const handleCheckboxFACategoryChange = (event: any) => {
    let faCategory = selectedFACategory
    event?.target?.checked ? faCategory.add(event?.target?.value) : faCategory.delete(event?.target?.value)
    setSelectedFACategory(new Set(faCategory))
  }

  const onSubmit = data => {
    history.push({
        pathname: '/digital-reports/fixed-assets/fixed-assets-summary-report/generated',
        state: {
            CompanyID: data?.CompanyID,
            AssetType: selectedFACategory ? Array.from(selectedFACategory) : undefined,
            Year: data.Year,
            MonthFrom: data.MonthFrom,
            MonthTo: data.MonthTo,
        },
    });
  };

  return (
    <>
      {CompanyLoading && <Loading/>}
      {CompanyAssignedLoading && <Loading />}
      {FACategoryDropdownListingLoading && <Loading />}
      {CostCentreLoading && <Loading />}
      {FALocationLoading && <Loading />}

      <MainHeader
        mainBtn="back"
        onClick={() => history.push(`/digital-reports/fixed-assets`)}
        smTitle={'Fixed Assets'}
        title={user?.companyName}
        routeSegments={[
          { name: 'Main Menu' },
          { name: 'Submenu' },
          { name: 'Fixed Assets Summary Report', current: true },
        ]}
        rightRouteSegments={[{ name: 'Parameters', current: true }]}
      />

      <ContentWrapper float>
        <CardContents>
            {!CompanyAssignedLoading && !CompanyLoading && (
            <Controller
              render={({ value, onChange }) => {
                const options: CompanyOption[] =
                  !!user?.superUser === false
                    ? getAssignedCompanyByUser
                    : getCompany;
                const defVal = getCompany?.filter(
                  x => x?.CompanyID === latestCompany,
                )[0];

                return (
                  <Autocomplete
                    options={options || []}
                    getOptionLabel={(option: CompanyOption) => option.Name}
                    fullWidth
                    onChange={(value, newValue: any) => {
                      setValue('CompanyID', newValue?.CompanyID);

                      loadCostCentre({
                        variables: {
                          CompanyID: newValue?.CompanyID,
                          orderByAsc: 'Code',
                          IsLastNode: true,
                        }
                      })

                      loadFALocation({
                        variables: {
                          CompanyID: newValue?.CompanyID
                        }
                      })
                    }}
                    renderOption={(props, option) => {
                      return <span>{props?.Name}</span>;
                    }}
                    defaultValue={defVal}
                    renderInput={(params: any) => {
                      return (
                        <div>
                          <TextField
                            {...params}
                            helperText={errors?.CompanyID?.message}
                            error={errors?.CompanyID ? true : false}
                            label="Company"
                            style={{ width: '100%' }}
                            margin="normal"
                            required
                            defaultValue={defVal}
                          />
                        </div>
                      );
                    }}
                  />
                );
              }}
              label="Company"
              name="CompanyID"
              autoComplete="off"
              control={control}
              multiline={true}
              fullWidth
              margin="normal"
              ref={register}
              helperText={errors?.CompanyID?.message}
              error={errors?.CompanyID ? true : false}
              required
              defaultValue={latestCompany}
            />
            )}

          <Controller
            render={({ value, onChange }) => {
              return (
                <Autocomplete
                  multiple
                  disableCloseOnSelect
                  options={getFACategoryDropdownListing || []}
                  getOptionLabel={option => option?.name}
                  fullWidth
                  onChange={(value, newValue: any) => {
                    const selecteted = new Set();

                    newValue?.map(x => {
                      selecteted.add(x?.fa_category_id);
                    });

                    setSelectedFACategory(new Set(selecteted));
                  }}
                  renderOption={(option, { selected }) => (
                    <React.Fragment>
                      <Checkbox
                        icon={icon}
                        checkedIcon={checkedIcon}
                        value={option?.fa_category_id}
                        style={{ marginRight: 8 }}
                        checked={selected}
                        color="primary"
                        onChange={event => {
                          handleCheckboxFACategoryChange(event);
                        }}
                      />
                      {option?.name}
                    </React.Fragment>
                  )}
                  renderInput={(params: any) => {
                    return (
                      <div>
                        <TextField
                          {...params}
                          variant="outlined"
                          style={{ width: '100%' }}
                          label="Asset Category"
                          margin="normal"
                        />
                      </div>
                    );
                  }}
                />
              );
            }}
            name="AssetType"
            label="Asset Category"
            margin="normal"
            autoComplete="off"
            fullWidth
            multiline={true}
            control={control}
            select
            ref={register}
          />

          <Controller
            as={<NumberFormat allowNegative={false} />}
            customInput={TextField}
            id="standard-basic"
            name="Year"
            label="Year"
            autoComplete="off"
            control={control}
            margin="normal"
            required
            ref={register}
            helperText={errors?.Year ? 'Year is required' : null}
            error={errors?.Year ? true : false}
            defaultValue={new Date().getFullYear()}
          />

          <Controller
            as={<NumberFormat allowNegative={false} />}
            customInput={TextField}
            id="standard-basic"
            name="MonthFrom"
            label="Month From"
            autoComplete="off"
            control={control}
            margin="normal"
            required
            ref={register}
            helperText={errors?.MonthFrom ? 'Month From is required' : null}
            error={errors?.MonthFrom ? true : false}
            defaultValue={1}
          />

          <Controller
            as={<NumberFormat allowNegative={false} />}
            customInput={TextField}
            id="standard-basic"
            name="MonthTo"
            label="Month To"
            autoComplete="off"
            control={control}
            margin="normal"
            required
            ref={register}
            helperText={errors?.MonthTo ? 'Month To is required' : null}
            error={errors?.MonthTo ? true : false}
            defaultValue={1}
          />
        </CardContents>

        <AccountFooter
          options={[
            {
              name: 'Submit',
              onClick: () => {
                handleSubmit(onSubmit)();
              },
              color: 'primary',
            },
          ]}
        />
      </ContentWrapper>
    </>
  )
}
